import React, { useState, useRef } from "react";
import styles from "./safetyVideos.module.scss";
import VideoModal from "./videoModal";
import { getThumbnailUrl } from "./youtube";
import { markSafetyVideoWatched } from "../../../network";

const PLAY_ICON =
  "https://d1flzashw70bti.cloudfront.net/next-gen/images/selfHelp/play_1789715872475.webp";
const LANGUAGE_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/selfHelp/language_1789715758860.svg";
const SUBTITLE_ICON =
  "https://d1flzashw70bti.cloudfront.net/original/images/selfHelp/language_1789715758860.svg";

export const BUS_INTRO = "BUS_INTRO";
export const EMERGENCY_EXIT = "EMERGENCY_EXIT";

export const getSafetyVideos = booking => {
  const service = booking?.trip?.service || {};

  return [
    {
      type: BUS_INTRO,
      link: service.busIntroVideoLink,
      title: "Bus Introduction",
      subtitle: "Get to know the features on your bus",
      isMandatory: false,
      isWatched: !!booking.isBusIntroVideoWatched
    },
    {
      type: EMERGENCY_EXIT,
      link: service.emergencyExitVideoLink,
      title: "Bus Safety Guidelines",
      subtitle: "Watch once for all passengers",
      isMandatory: true,
      isWatched: !!booking.isEmergencyExitVideoWatched
    }
  ].filter(video => !!video.link);
};

const SafetyVideos = props => {
  const { booking, onVideoWatched } = props;

  const videos = getSafetyVideos(booking);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const trackRef = useRef(null);
  const dragRef = useRef(null);

  if (!videos.length) {
    return null;
  }

  // Touch and trackpad scroll the track natively; the dots and a mouse drag
  // just move the same scroll position.
  const syncActiveIndex = () => {
    const track = trackRef.current;

    if (track && track.clientWidth) {
      setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
    }
  };

  const goToIndex = index => {
    const track = trackRef.current;

    if (track) {
      track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
    }

    setActiveIndex(index);
  };

  const startDrag = event => {
    // Touch already scrolls natively — dragging is only for a mouse.
    if (event.pointerType === "touch" || !trackRef.current) {
      return;
    }

    dragRef.current = {
      startX: event.clientX,
      startScroll: trackRef.current.scrollLeft,
      moved: false
    };
    setIsDragging(true);
  };

  const onDrag = event => {
    const drag = dragRef.current;

    if (!drag || !trackRef.current) {
      return;
    }

    const delta = event.clientX - drag.startX;

    if (Math.abs(delta) > 3) {
      drag.moved = true;
    }

    trackRef.current.scrollLeft = drag.startScroll - delta;
  };

  const endDrag = () => {
    const track = trackRef.current;

    if (dragRef.current && track && track.clientWidth) {
      // Snapping is disabled mid-drag, so settle on the nearest card here.
      goToIndex(
        Math.max(
          0,
          Math.min(
            videos.length - 1,
            Math.round(track.scrollLeft / track.clientWidth)
          )
        )
      );
    }

    setIsDragging(false);
  };

  // A drag that ends on the card would otherwise also fire its click.
  const openVideo = video => {
    const dragged = dragRef.current && dragRef.current.moved;
    dragRef.current = null;

    if (!dragged) {
      setPlayingVideo(video);
    }
  };

  const confirmWatched = async watched => {
    setPlayingVideo(null);

    try {
      await markSafetyVideoWatched({
        bookingId: booking.id || booking._id,
        videoType: watched.type
      });
      onVideoWatched(watched.type);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.wrapper}>
      <span className={styles.heading}>Bus Guidelines</span>

      <div className={styles.carousel}>
        <div
          ref={trackRef}
          className={isDragging ? styles.trackDragging : styles.track}
          onScroll={syncActiveIndex}
          onPointerDown={startDrag}
          onPointerMove={onDrag}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          {videos.map(video => (
            <div
              className={styles.card}
              key={video.type}
              onClick={() => openVideo(video)}
            >
              <div className={styles.thumbnail}>
                {getThumbnailUrl(video.link) ? (
                  <img
                    className={styles.thumbnailImage}
                    src={getThumbnailUrl(video.link)}
                    alt=""
                  />
                ) : null}
                <img className={styles.play} src={PLAY_ICON} alt="play" />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitles}>
                  <span className={styles.cardTitle}>{video.title}</span>
                  <span className={styles.cardSubtitle}>{video.subtitle}</span>
                </div>
                <div className={styles.cardMeta}>
                  <div className={styles.metaItem}>
                    <img src={LANGUAGE_ICON} alt="" />
                    <span>English +4 Other</span>
                  </div>
                  <span className={styles.metaDivider}>|</span>
                  <div className={styles.metaItem}>
                    <img src={SUBTITLE_ICON} alt="" />
                    <span>Subtitles</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {videos.length > 1 ? (
          <div className={styles.dots}>
            {videos.map((item, index) => (
              <div
                key={item.type}
                className={
                  index === activeIndex ? styles.dotActive : styles.dot
                }
                onClick={() => goToIndex(index)}
              />
            ))}
          </div>
        ) : null}
      </div>

      {playingVideo ? (
        <VideoModal
          video={playingVideo}
          // Opened by the customer themselves, so it stays dismissible even
          // for the emergency video; enforcement lives in the boarding popup.
          isMandatory={false}
          onConfirm={confirmWatched}
          onClose={() => setPlayingVideo(null)}
        />
      ) : null}
    </div>
  );
};

export default SafetyVideos;
